/**
 * Derives task conversation panel state from the messages API and list selection.
 * @module useTaskConversationView
 */

import { useCallback, useEffect, useMemo } from "react";

import { fetchOncoemrTaskMessages } from "../../../../../api/getCall/fetchOncoemrTaskMessages";
import type { OncoemrTaskMessagesResponse } from "../../../../../data-model/oncoemrTaskMessagesModel";
import { useAbortableFetch } from "../../../../../hooks/useAbortableFetch";
import type { TaskConversationData } from "../components/tasksConversationPanel";
import type { TaskListPanelItem } from "../components/tasksListPanel";
import {
  buildPanelBackedConversation,
  mapThreadResponseToTaskConversation,
} from "../helpers/mapOncoemrThreadToTaskConversation";

/** Background refresh interval while a task thread is open (inbound + outbox updates). */
const THREAD_POLL_INTERVAL_MS = 60_000;

/** Inputs for {@link useTaskConversationView}. */
export interface UseTaskConversationViewParams {
  panelItem?: TaskListPanelItem;
  taskRowId?: string;
  orgId: string;
}

/** Return value from {@link useTaskConversationView}. */
export interface UseTaskConversationViewResult {
  /** Conversation payload for the panel, or undefined when nothing is selected. */
  conversation?: TaskConversationData;
  /** Re-fetches `GET .../messages` for the current selection (e.g. after queuing a reply). */
  refetchMessages: () => Promise<void>;
}

/**
 * Loads `GET .../messages` when a task row is selected and merges loading/error UX.
 *
 * After the thread loads for the current selection, polls every
 * {@link THREAD_POLL_INTERVAL_MS} for inbound messages and delivery status updates.
 *
 * @param params - Selected panel row, task primary key, and org scope.
 * @returns Conversation payload and a refetch helper for the messages thread.
 */
export const useTaskConversationView = ({
  panelItem,
  taskRowId,
  orgId,
}: UseTaskConversationViewParams): UseTaskConversationViewResult => {
  const fetchMessages = useCallback(
    async (signal: AbortSignal) => {
      if (!taskRowId || !orgId) {
        throw new Error("Missing task or organization context.");
      }
      return fetchOncoemrTaskMessages(taskRowId, { org_id: orgId }, signal);
    },
    [taskRowId, orgId],
  );

  const {
    runFetch,
    abort,
    data: thread,
    loading,
    error,
  } = useAbortableFetch<OncoemrTaskMessagesResponse>(fetchMessages);

  useEffect(() => {
    if (!panelItem || !taskRowId || !orgId) {
      abort();
      return undefined;
    }

    void runFetch();
    return () => abort();
  }, [panelItem, taskRowId, orgId, runFetch, abort]);

  const isThreadReadyForPoll = useMemo(
    () =>
      Boolean(
        thread &&
        taskRowId &&
        thread.task.id === taskRowId &&
        panelItem &&
        orgId,
      ),
    [thread, taskRowId, panelItem, orgId],
  );

  useEffect(() => {
    if (!isThreadReadyForPoll) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void runFetch();
    }, THREAD_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isThreadReadyForPoll, runFetch]);

  const refetchMessages = useCallback(async () => {
    if (!panelItem || !taskRowId || !orgId) {
      return;
    }
    await runFetch();
  }, [panelItem, taskRowId, orgId, runFetch]);

  const conversation = useMemo(() => {
    if (!panelItem || !taskRowId) {
      return undefined;
    }

    const messagesError =
      error && error.name !== "AbortError"
        ? String(error.message ?? "Could not load messages.")
        : null;

    const isThreadStale = Boolean(thread && thread.task.id !== taskRowId);

    if (messagesError && (isThreadStale || !thread)) {
      return {
        ...buildPanelBackedConversation(panelItem),
        messagesLoading: false,
        messagesError,
        messages: [],
      };
    }

    if (isThreadStale || (loading && !thread) || (!thread && !messagesError)) {
      return {
        ...buildPanelBackedConversation(panelItem),
        messagesLoading: true,
        messagesError: null,
      };
    }

    if (!thread) {
      return {
        ...buildPanelBackedConversation(panelItem),
        messagesLoading: false,
        messagesError: messagesError ?? "Could not load messages.",
        messages: [],
      };
    }

    return {
      ...mapThreadResponseToTaskConversation(taskRowId, thread),
      messagesLoading: false,
      messagesError: null,
    };
  }, [panelItem, taskRowId, thread, loading, error]);

  return { conversation, refetchMessages };
};
